/**
 * «Подобрать маршрут» в режиме «Пул» (ТЗ Беки «маршруты сбора», 06.10).
 *
 * Водитель жмёт кнопку и получает выборку на одну машину: что везти на базу
 * и что вернуть в зону. Выборка — та же, что набирается пальцем
 * (`lib/pickup-selection.ts`): до «Забрать» её можно править, а порядок
 * объезда после «Забрать» назначает сервер — как и для ручной выборки.
 *
 * Волна 4: сюда же пришли «куда везём» (`poolDestination`: в хаб или на
 * базу), перестановка (заряженные байки нижнего города рядом с выбранными)
 * и порядок для предпросмотра (`poolOrder`).
 *
 * Хабы (этап 1, 08.10): какой хаб у байка и маршрут с выгрузками решает
 * сервер (`fleet/internal/biz/pool_plan.go`) на «Забрать», здесь — только
 * отбор: заряженные с базы и кучки снизу едут в хабы с нехваткой, а место в
 * машине считается по тому же правилу, что и у сервера (`peakLoad`).
 *
 * Категории ТЗ: неисправный; разряженный (`available`, заряд ≤ порога);
 * исправный за зоной. Исключения: лейбл «Штрафстоянка», кучка у базы,
 * всё, что не `available`/`faulty`, чужие пулы (их отсекает `selectable` —
 * та же допустимость, что у метки на карте, `poolEligibility`).
 *
 * Лист: экран передаёт сюда уже прочитанные парк, зоны и допустимость.
 */
import { LOW_BATTERY_PCT } from './describe-bike'
import { distanceM, hasPosition, type LatLng } from './geo'
import { nearestNeighbourOrder } from './navigation'
import { isOutsideRidingArea, type RideZone } from './zones'

/** База: старт и финиш маршрута (ул. 20-я линия, 68; FLEET_POOL_OFFICE_*). */
export const BASE: LatLng = { lat: 43.2319, lng: 76.8874 }

/** Кучка у базы — уже приехала, её не собирают (ТЗ: «радиус ~300 м»). */
export const BASE_RADIUS_M = 300

/**
 * Велик на базе — по статусу `at_base`, а не по радиусу от `BASE`: так
 * считают пул и сервер (`isChargedAtBase`, `poolDestination`), а позиция
 * велика на зарядке — шум (на 10.10 один `at_base` числится в Астане).
 * Карта скаута по этому же правилу прячет велики в домик базы.
 */
export function isAtBase(b: { status: string }): boolean {
  return b.status === 'at_base'
}

/** Вместимость машины: столько в машине одновременно; сервер присылает свою (`carCapacity`). */
export const CAR_CAPACITY = 12

/** С какого свежего заряда байк с базы везут в хаб; сервер присылает свой (`baseChargedMinPct`). */
export const BASE_CHARGED_MIN_PCT = 80

/** Лейбл диспетчера; по имени, пока статуса «штрафстоянка» нет. */
const IMPOUND_LABEL = 'штрафстоянка'

/** Отбор ТЗ: «только available или faulty». */
const PICKABLE = new Set(['available', 'faulty'])

/** Севернее этой широты (lat > …) — нижний город; южнее — верхний. */
export const LOWER_CITY_LAT = 43.262

/** В хаб: байк везём в хаб только при заряде строго выше этого (%). */
export const UNLOAD_MIN_BATTERY = 20

/** Перестановка: берём только заряженные от этого заряда. */
export const REPOSITION_MIN_BATTERY = 50

/** Перестановка: не дальше этого от уже выбранного байка. */
export const REPOSITION_RADIUS_M = 500

/** Кучка в нижнем городе: не меньше стольких исправных байков… */
export const CLUSTER_MIN_SIZE = 4

/** …в этом радиусе от одного из них. */
export const CLUSTER_RADIUS_M = 150

/** unload — в хаб (какой — решает сервер), base — на базу. */
export type Destination = 'unload' | 'base'

type PickBike = LatLng & {
  id: string
  status: string
  batteryPct: number
  /** Тот же трёхзначный флаг, что у ScoutVehicle: только `false` — заряд свежий. */
  runtimeStale?: boolean | undefined
  labels: readonly { name: string }[]
  /** RFC3339: когда водитель последний раз поставил этот байк; пусто — никогда. */
  poolDeliveredAt?: string | undefined
  /** Почему байк неисправен (`ScoutVehicle.faultReason`); пусто — не сказано. */
  faultReason?: string | undefined
}

function isImpounded(b: PickBike): boolean {
  return b.labels.some((l) => l.name.trim().toLowerCase() === IMPOUND_LABEL)
}

/**
 * Разряжен — только по свежему снимку: протухший снимок отдаёт 0 %, и по
 * сырому полю «разряженными» стали бы все молчащие байки (`describe-bike.ts`).
 */
function isLowBattery(b: PickBike): boolean {
  return b.runtimeStale === false && b.batteryPct <= LOW_BATTERY_PCT
}

/** Заряд известен и свежий. */
function hasFreshBattery(b: PickBike): boolean {
  return b.runtimeStale === false
}

/**
 * Слишком разряжен для хаба — едет на базу: заряд ≤ 20 % или неизвестен.
 *
 * ⚠ ЭТАП 2 («садящиеся» по графику смен водителей): порог станет 20–25 % —
 * от времени до следующей смены, — и байки 20–25 % (в том числе из кучек)
 * поедут на базу. Менять здесь: это правило читают и `poolDestination`, и
 * отбор кучек.
 */
export function tooLowForHub(b: Pick<PickBike, 'batteryPct' | 'runtimeStale'>): boolean {
  return b.runtimeStale !== false || b.batteryPct <= UNLOAD_MIN_BATTERY
}

/**
 * Заряженный на базе: `at_base` со свежим зарядом не ниже порога. Его пул
 * берёт, чтобы отвезти в хаб в начале рейса (зеркало `chargedForHub` во fleet).
 */
export function isChargedAtBase(
  b: Pick<PickBike, 'status' | 'batteryPct' | 'runtimeStale'>,
  minPct: number = BASE_CHARGED_MIN_PCT,
): boolean {
  return b.status === 'at_base' && b.runtimeStale === false && b.batteryPct >= minPct
}

/**
 * «Неисправен» не из-за поломки, а удержание маршрута: ТЗ маршрутов сбора велит
 * после подтверждения маршрута перевести его байки в «Неисправен» с причиной
 * «Перевозка, передислокация» или «Разряжен», чтобы их не арендовали до машины.
 * Пул сам прячет байк (`in_pool`), поэтому такой байк везём по правилам
 * свободного. Флот узнаёт удержание по той же причине (`fleet/internal/biz/pool.go`).
 */
export function isRouteHold(b: Pick<PickBike, 'status' | 'faultReason'>): boolean {
  if (b.status !== 'faulty') return false
  const r = (b.faultReason ?? '').trim().toLowerCase()
  return r.includes('перевоз') || r.includes('передислок') || r.startsWith('разря')
}

/**
 * Куда везём байк: в хаб или на базу. Одно правило для любого выбранного
 * байка — и из «Подобрать маршрут», и набранного пальцем.
 *
 * В хаб — исправный (`available` или удержание маршрута) со свежим зарядом
 * > 20 %, если он за зоной катания или взят как перестановка (кучка); и
 * заряженный с базы (`at_base` выбирается только заряженным, `lib/pool.ts`).
 * Остальное — на базу.
 */
export function poolDestination(
  b: Pick<PickBike, 'status' | 'batteryPct' | 'runtimeStale' | 'lat' | 'lng' | 'faultReason'>,
  opts: { zones: readonly RideZone[] | undefined; reposition: boolean },
): Destination {
  if (b.status === 'at_base') return 'unload'
  if (b.status !== 'available' && !isRouteHold(b)) return 'base'
  if (tooLowForHub(b)) return 'base'
  return opts.reposition || isOutsideRidingArea(b, opts.zones) ? 'unload' : 'base'
}

/**
 * Порядок для предпросмотра до «Забрать»: сначала «в хаб» — «ближайший
 * следующий» от водителя (нет позиции — от базы), потом «на базу» — от
 * последнего из них. Хабы и окончательный порядок назначит сервер.
 */
export function poolOrder<T extends LatLng & { id: string }>(
  items: readonly T[],
  destinationOf: (id: string) => Destination,
  from: LatLng | null,
): T[] {
  const start = from ?? BASE
  const unload = nearestNeighbourOrder(start, items.filter((i) => destinationOf(i.id) === 'unload'))
  const base = items.filter((i) => destinationOf(i.id) !== 'unload')
  return [...unload, ...nearestNeighbourOrder(unload.at(-1) ?? start, base)]
}

/** Сутки по Алматы (UTC+5, без перевода часов) — `YYYY-MM-DD`; мусор — пусто. */
export function almatyDayKey(rfc3339: string): string {
  const t = Date.parse(rfc3339)
  if (Number.isNaN(t)) return ''
  return new Date(t + 5 * 3_600_000).toISOString().slice(0, 10)
}

/** Куда везём каждый из выбранных (для тела «Забрать»); нет в парке — база. */
export function candidateDestinations(
  bikes: readonly (Pick<PickBike, 'id' | 'status' | 'batteryPct' | 'runtimeStale' | 'lat' | 'lng' | 'faultReason'>)[],
  ids: readonly string[],
  opts: { zones: readonly RideZone[] | undefined; reposition: ReadonlySet<string> },
): Record<string, Destination> {
  const byId = new Map(bikes.map((b) => [b.id, b]))
  const out: Record<string, Destination> = {}
  for (const id of ids) {
    const b = byId.get(id)
    out[id] = b ? poolDestination(b, { zones: opts.zones, reposition: opts.reposition.has(id) }) : 'base'
  }
  return out
}

/*
 * ─── Место в машине ─────────────────────────────────────────────────────
 * Зеркало сервера (`peakLoad` в `fleet/internal/biz/pool_plan.go`): группы
 * рейса — заряженные с базы (в хаб), уличные в хаб, на базу. Сначала
 * выгружаются группы, которые целиком в машине, потом заряженные (база →
 * хаб), потом уличные (подборы → хабы), последними — на базу. Хаб
 * освобождает место, поэтому лимит — пик загрузки, а не число байков.
 * Хабов нет — «в хаб» едут на базу.
 */

type Group = 'charged' | 'field' | 'base'

/** Сколько в рейсе по группам: в машине (`car*`) и ещё забрать. */
export type CarLoad = Record<'carCharged' | 'carField' | 'carBase' | 'charged' | 'field' | 'base', number>

export const EMPTY_LOAD: CarLoad = { carCharged: 0, carField: 0, carBase: 0, charged: 0, field: 0, base: 0 }

function groupOf(dest: Destination, charged: boolean, hubs: boolean): Group {
  if (dest !== 'unload' || !hubs) return 'base'
  return charged ? 'charged' : 'field'
}

function withBike(l: CarLoad, g: Group, inCar: boolean): CarLoad {
  const key = inCar ? (g === 'charged' ? 'carCharged' : g === 'field' ? 'carField' : 'carBase') : g
  return { ...l, [key]: l[key] + 1 }
}

/** Больше всего байков в машине в одной точке рейса. */
export function peakLoad(l: CarLoad): number {
  let load = l.carCharged + l.carField + l.carBase
  let peak = load
  if (l.charged === 0) load -= l.carCharged
  if (l.field === 0) load -= l.carField
  if (l.charged > 0) {
    peak = Math.max(peak, load + l.charged)
    load -= l.carCharged
  }
  if (l.field > 0) {
    peak = Math.max(peak, load + l.field)
    load -= l.carField
  }
  return Math.max(peak, load + l.base)
}

/** Пункт своего пула — для места в машине. */
export type PoolLoadItem = { state: string; destination: string; prevStatus: string }

/**
 * Загрузка рейса: свой пул (в машине и едем) и выбранное к «Забрать»
 * (`dest`, `charged` — байк с базы). `haveHubs` — хабы есть, иначе «в хаб»
 * едут на базу.
 */
export function routeLoad(
  pool: readonly PoolLoadItem[],
  selected: readonly { dest: Destination; charged: boolean }[],
  haveHubs: boolean,
): CarLoad {
  let load = EMPTY_LOAD
  for (const it of pool) {
    if (it.state !== 'pooled' && it.state !== 'picked') continue
    const dest: Destination = it.destination === 'unload' ? 'unload' : 'base'
    load = withBike(load, groupOf(dest, it.prevStatus === 'at_base', haveHubs), it.state === 'picked')
  }
  for (const s of selected) load = withBike(load, groupOf(s.dest, s.charged, haveHubs), false)
  return load
}

/** Сколько из загрузки едет в хабы — она занимает нехватку хабов. */
function toHubs(l: CarLoad): number {
  return l.carCharged + l.carField + l.charged + l.field
}

/** Хаб для отбора: сколько стоит и сколько должно (`/v1/scout/hubs`). */
export type HubFill = { target: number; present: number }

/** Сколько всем хабам не хватает до нормы; неизвестная норма (0) — не нехватка. */
export function hubShortage(hubs: readonly HubFill[]): number {
  return hubs.reduce((sum, h) => sum + Math.max(0, h.target - h.present), 0)
}

/**
 * Кучки: жадно — байк, у которого больше всех соседей в `CLUSTER_RADIUS_M`,
 * с этими соседями; меньше `CLUSTER_MIN_SIZE` — уже не кучка. Самые большие
 * кучки первыми.
 */
export function lowerCityClusters<T extends LatLng>(bikes: readonly T[]): T[][] {
  let left = [...bikes]
  const out: T[][] = []
  for (;;) {
    let best: T[] = []
    for (const c of left) {
      const near = left.filter((b) => distanceM(c, b) <= CLUSTER_RADIUS_M)
      if (near.length > best.length) best = near
    }
    if (best.length < CLUSTER_MIN_SIZE) return out
    out.push(best)
    left = left.filter((b) => !best.includes(b))
  }
}

export type PickPlan = {
  /** Id в порядке объезда (`poolOrder`). */
  ids: string[]
  /** Те из них, что взяты как перестановка или кучка — для `poolDestination`. */
  reposition: Set<string>
  /** Машина полна ещё до подбора — брать некуда. */
  full: boolean
}

/**
 * Что взять на машину, по очереди:
 *
 * 1. Категории ТЗ — неисправный, разряженный, исправный за зоной.
 * 2. Заряженные с базы (`isChargedAtBase`) — в хабы, не больше их нехватки.
 * 3. Перестановка — заряженные (≥ 50 %) нижнего города (севернее
 *    LOWER_CITY_LAT) в 500 м от уже выбранного (цепочкой «сосед соседа» не
 *    тянемся — иначе выборка поползёт через весь город) — и кучки
 *    нижнего города (`lowerCityClusters`: ≥ 4 исправных в 150 м). Тоже в
 *    хабы и тоже не больше оставшейся нехватки.
 *
 * Байк берётся, только если с ним в машине ни в одной точке рейса не больше
 * `capacity` (`peakLoad`): считаются свой пул (`pool`: в машине и едем) и
 * уже выбранное (`exclude` с назначениями `excludeDest`). Кнопка добавляет к
 * ручной выборке, а не заменяет её. Хабов нет (`hubs` пуст) — в хабы
 * никого не добираем.
 *
 * Поставленные сегодня (по Алматы) водителем байки не берутся вовсе — их
 * только что туда отвезли. Кроме заряженных на базе: сданный ночью «На
 * зарядку» к утру заряжен, и его как раз везут в хаб.
 */
export function autoPickPlan(
  bikes: readonly PickBike[],
  opts: {
    zones: readonly RideZone[] | undefined
    me: LatLng | null
    selectable: (id: string) => boolean
    exclude: ReadonlySet<string>
    excludeDest?: Readonly<Record<string, Destination>>
    hubs?: readonly HubFill[]
    pool?: readonly PoolLoadItem[]
    capacity?: number
    baseChargedMinPct?: number
    now?: Date
  },
): PickPlan {
  const capacity = opts.capacity ?? CAR_CAPACITY
  const hubs = opts.hubs ?? []
  const haveHubs = hubs.length > 0
  const byId = new Map(bikes.map((b) => [b.id, b]))
  const selected = [...opts.exclude].flatMap((id) => {
    const b = byId.get(id)
    // Серую отметку «Забрать» не отправит — места она не займёт.
    if (!b || !opts.selectable(id)) return []
    const dest = opts.excludeDest?.[id] ?? poolDestination(b, { zones: opts.zones, reposition: false })
    return [{ dest, charged: b.status === 'at_base' }]
  })
  let load = routeLoad(opts.pool ?? [], selected, haveHubs)
  let shortage = hubShortage(hubs) - toHubs(load)
  const full = peakLoad(load) >= capacity

  const today = almatyDayKey((opts.now ?? new Date()).toISOString())
  const open = bikes.filter(
    (b) =>
      opts.selectable(b.id) &&
      !opts.exclude.has(b.id) &&
      hasPosition(b) &&
      !isImpounded(b) &&
      // Отвезённый сегодня на базу и уже заряженный едет в хаб: правило — про улицу.
      (b.status === 'at_base' || !(b.poolDeliveredAt && almatyDayKey(b.poolDeliveredAt) === today)),
  )
  const from = opts.me ?? BASE
  const chosen: PickBike[] = []
  const taken = new Set<string>()
  const reposition = new Set<string>()
  const take = (b: PickBike, dest: Destination): boolean => {
    const g = groupOf(dest, b.status === 'at_base', haveHubs)
    const next = withBike(load, g, false)
    if (peakLoad(next) > capacity) return false
    load = next
    if (g !== 'base') shortage--
    chosen.push(b)
    taken.add(b.id)
    return true
  }

  // 1. Категории ТЗ.
  for (const b of nearestNeighbourOrder(
    from,
    open.filter(
      (b) =>
        PICKABLE.has(b.status) &&
        distanceM(BASE, b) > BASE_RADIUS_M &&
        (b.status === 'faulty' || isLowBattery(b) || isOutsideRidingArea(b, opts.zones)),
    ),
  )) {
    take(b, poolDestination(b, { zones: opts.zones, reposition: false }))
  }

  // 2. Заряженные с базы — самые заряженные первыми.
  if (haveHubs) {
    const charged = open
      .filter((b) => isChargedAtBase(b, opts.baseChargedMinPct ?? BASE_CHARGED_MIN_PCT))
      .sort((a, b) => b.batteryPct - a.batteryPct)
    for (const b of charged) {
      if (shortage <= 0) break
      take(b, 'unload')
    }
  }

  // 3. Перестановка и кучки нижнего города — в хабы с нехваткой.
  const lower = (b: PickBike) =>
    b.status === 'available' && !taken.has(b.id) && hasFreshBattery(b) && b.lat > LOWER_CITY_LAT
  const anchors = [...chosen, ...bikes.filter((b) => opts.exclude.has(b.id) && hasPosition(b))]
  const repo = nearestNeighbourOrder(
    from,
    open.filter(
      (b) =>
        lower(b) &&
        b.batteryPct >= REPOSITION_MIN_BATTERY &&
        anchors.some((a) => distanceM(a, b) <= REPOSITION_RADIUS_M),
    ),
  )
  const clusters = lowerCityClusters(open.filter((b) => lower(b) && !tooLowForHub(b)))
  for (const b of [...repo, ...clusters.flat()]) {
    if (shortage <= 0) break
    if (!taken.has(b.id) && take(b, 'unload')) reposition.add(b.id)
  }

  const dest = (id: string): Destination => {
    const b = chosen.find((x) => x.id === id)
    return b ? poolDestination(b, { zones: opts.zones, reposition: reposition.has(id) }) : 'base'
  }
  return { ids: poolOrder(chosen, dest, opts.me).map((b) => b.id), reposition, full }
}

/** Только id — см. `autoPickPlan`. */
export function autoPickRoute(
  bikes: readonly PickBike[],
  opts: Parameters<typeof autoPickPlan>[1],
): string[] {
  return autoPickPlan(bikes, opts).ids
}
